/** Where a plotted coordinate came from, in the words the CEO map uses for it.
 *
 *  The board counts every mapped deal by origin — `geocoded` when a geocoder
 *  wrote the point from the property's address, `stored` when it was already on
 *  the property — and names the service for the geocoded ones. Nobody reviews a
 *  geocoded point before it is plotted, so the map says so. A property with no
 *  geocoder record reads as stored and no more: its coordinate was typed in,
 *  carried from intake or predates provenance, and the record cannot say which.
 *
 *  Every field is optional. A board built before they shipped makes no claim
 *  about origin rather than defaulting to one.
 */
export interface CoordinateOriginMix {
  coordinateOrigin?: { geocoded?: number; stored?: number }
  /** Geocoded deals by the service recorded on the property, as recorded. */
  geocodedBy?: Record<string, number>
}

const GEOCODED_LABEL = 'Geocoded from address — not reviewed'
const STORED_LABEL = 'Stored on the property record'

/** The services the backend records against a coordinate it geocoded. One this
 *  table does not know is shown as recorded, never dropped. */
const GEOCODER_NAMES = new Map([
  ['census', 'US Census Geocoder'],
  ['google', 'Google Geocoding'],
  ['google-places', 'Google Places'],
  ['unrecorded', 'service not recorded'],
])

function originCounts(mix: CoordinateOriginMix): { geocoded: number; stored: number } {
  return {
    geocoded: mix.coordinateOrigin?.geocoded ?? 0,
    stored: mix.coordinateOrigin?.stored ?? 0,
  }
}

function serviceNames(mix: CoordinateOriginMix): string {
  const services = Object.entries(mix.geocodedBy ?? {})
  if (services.length === 0) return ''
  const name = (code: string) => GEOCODER_NAMES.get(code) ?? code
  const named =
    services.length === 1
      ? name(services[0][0])
      : services.map(([code, count]) => `${name(code)} ${count}`).join(', ')
  return ` (${named})`
}

/** The full statement, for a bubble's tooltip and the panel footnote. A single
 *  origin reads as a plain label; a mix says how many deals each covers. `deals`
 *  is the noun the counts are of ("deals" for one city, "mapped deals" for the
 *  whole map). Null when the board carries no origin, so no claim is printed. */
export function originText(mix: CoordinateOriginMix, deals: string): string | null {
  const { geocoded, stored } = originCounts(mix)
  const total = geocoded + stored
  if (total === 0) return null
  const geocodedText = `${GEOCODED_LABEL}${serviceNames(mix)}`
  if (stored === 0) return geocodedText
  if (geocoded === 0) return STORED_LABEL
  return `${geocodedText}: ${geocoded} of ${total} ${deals} · ${STORED_LABEL}: ${stored} of ${total} ${deals}`
}

/** The map's legend: which origins appear among the plotted points, and nothing
 *  else, so it stays one short line over the plot. */
export function originLegend(mix: CoordinateOriginMix): string | null {
  const { geocoded, stored } = originCounts(mix)
  const labels: string[] = []
  if (geocoded > 0) labels.push(GEOCODED_LABEL)
  if (stored > 0) labels.push(STORED_LABEL)
  return labels.length > 0 ? labels.join(' · ') : null
}
